/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Moment from "react-moment";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { ENDPOINT } from "../../services/api-endpoint";
import {
  addList,
  getList,
  getListById,
  updateList,
} from "../../services/services";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import RecipeGradeMultiSelect from "../../components/recipe-grade-multi-select/recipe-grade-multi-select";
import { useMaterialCatalog } from "../../hooks/useMaterialCatalog";
import {
  PART_NUMBER_PLACEHOLDER,
  sanitizeStoredPartNumber,
  suggestNextPartNumber,
} from "../../utils/partNumberSuggestion";
import { parsePartMaterialIds } from "../../utils/partMaterials";
import "./mes-parts-catalog.scss";

type PartRow = {
  id: number;
  partname?: string | null;
  partnumber?: string | null;
  materialcategory?: string | null;
  material?: string | null;
  materialCategoryName?: string | null;
  materialName?: string | null;
  materialNames?: string[];
  drawingref?: string | null;
  defaulttolerance?: string | null;
  imagrurl?: string | null;
  description?: string | null;
  createdate?: string;
  active?: boolean;
};

async function fetchPartsCatalogForSuggestion(): Promise<PartRow[]> {
  const resp = await getList(ENDPOINT.PARTS_LIST, {
    pagesize: 500,
    pagenumber: 0,
    serachtext: "",
  });
  return Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
}

function formatMaterialCell(
  p: PartRow,
  resolveCategoryLabel: (v: string | null | undefined) => string,
  resolveMaterialLabel: (
    cat: string | null | undefined,
    mat: string | null | undefined,
  ) => string,
): string {
  const cat =
    (p.materialCategoryName ?? "").trim() ||
    resolveCategoryLabel(p.materialcategory ?? "");
  const namesFromApi = Array.isArray(p.materialNames)
    ? p.materialNames.map((n) => String(n).trim()).filter(Boolean)
    : [];
  const mat =
    namesFromApi.length > 0
      ? namesFromApi.join(", ")
      : (p.materialName ?? "").trim() ||
        (() => {
          const ids = parsePartMaterialIds(p.material);
          if (ids.length === 0) {
            return (
              resolveMaterialLabel(p.materialcategory ?? "", p.material ?? "") ||
              (p.material ?? "").trim()
            );
          }
          return ids
            .map(
              (id) =>
                resolveMaterialLabel(p.materialcategory ?? "", String(id)) ||
                String(id),
            )
            .join(", ");
        })();
  if (cat && mat) return `${cat} · ${mat}`;
  if (cat) return cat;
  if (mat) return mat;
  return "—";
}

const MesParts = () => {
  const [validated, setValidated] = useState(false);
  const [formInputs, setForm] = useState<Record<string, string>>({});
  const [editId, setEditId] = useState<number | "">("");
  const [partsCollection, setMesParts] = useState<PartRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openAddUpdateModal, setAddUpdateModal] = useState<boolean>(false);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
  const [busyMessage, setBusyMessage] = useState("Loading parts...");
  const { isLoading, runWithLoading } = usePageLoader();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const {
    categories: materialCategories,
    grades: materialGrades,
    resolveCategoryLabel,
    resolveMaterialLabel,
  } = useMaterialCatalog(formInputs.materialcategory || "");

  useEffect(() => {
    setSelectedMaterialIds((prev) =>
      prev.filter((id) => materialGrades.some((g) => g.id === id)),
    );
  }, [materialGrades]);

  const loadPartsData = useCallback(
    async (nextPageIndex: number, overrideSearchText?: string) => {
      const effectiveSearchText =
        overrideSearchText !== undefined ? overrideSearchText : searchText;

      const request = {
        pagesize: pageSize,
        pagenumber: nextPageIndex,
        serachtext: effectiveSearchText,
      };

      const resp = await getList(ENDPOINT.PARTS_LIST, request);
      const items = Array.isArray(resp) ? resp : (resp as any)?.data || [];
      const count = Array.isArray(resp)
        ? items.length
        : Number((resp as any)?.count || 0);

      setMesParts(items);
      setTotalCount(count);
      setPageIndex(nextPageIndex);
    },
    [searchText, pageSize],
  );

  const handleClose = () => {
    setEditId("");
    setForm({});
    setSelectedMaterialIds([]);
    setAddUpdateModal(false);
    setValidated(false);
  };

  const handleShow = () => {
    setValidated(false);
    setEditId("");
    setForm({
      partname: "",
      partnumber: "",
      materialcategory: "",
      drawingref: "",
      defaulttolerance: "",
      imagrurl: "",
      description: "",
    });
    setSelectedMaterialIds([]);
    setAddUpdateModal(true);
    void (async () => {
      try {
        const rows = await fetchPartsCatalogForSuggestion();
        const next = suggestNextPartNumber(rows);
        setForm((prev) => ({
          ...prev,
          partnumber: sanitizeStoredPartNumber(prev.partnumber) || next,
        }));
      } catch {
        setForm((prev) => ({
          ...prev,
          partnumber:
            sanitizeStoredPartNumber(prev.partnumber) ||
            suggestNextPartNumber(partsCollection),
        }));
      }
    })();
  };

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    if (name === "materialcategory") {
      setForm((values) => ({ ...values, materialcategory: value }));
      setSelectedMaterialIds([]);
      return;
    }
    setForm((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }
    void onSave();
  };

  async function onSave() {
    try {
      const pn = sanitizeStoredPartNumber(String(formInputs.partnumber || ""));
      const request: Record<string, unknown> = {
        partname: String(formInputs.partname || "").trim(),
        partnumber: pn || null,
        materialcategory: String(formInputs.materialcategory || "").trim() || null,
        materialIds: selectedMaterialIds,
        drawingref: String(formInputs.drawingref || "").trim() || null,
        defaulttolerance: String(formInputs.defaulttolerance || "").trim() || null,
        imagrurl: String(formInputs.imagrurl || "").trim() || null,
        description: String(formInputs.description || "").trim() || null,
        active: true,
      };

      if (editId) {
        request.id = editId;
      }

      let saved = false;
      setBusyMessage("Saving part...");
      await runWithLoading(async () => {
        let saveParts: string | unknown;
        if (editId) {
          saveParts = await updateList(ENDPOINT.PARTS_UPDATE, request);
        } else {
          saveParts = await addList(ENDPOINT.PARTS_ADD, request);
        }
        if (saveParts !== "success") return;
        setBusyMessage("Loading parts...");
        await loadPartsData(0, "");
        saved = true;
      });

      if (saved) {
        setSearchText("");
        toast.success(
          editId ? "Part updated successfully." : "Part created successfully.",
        );
        handleClose();
      } else {
        toast.error("Could not save part. Please try again.");
      }
    } catch (e) {
      console.log(`Error in addUpdate: ${e}`);
      toast.error("Something went wrong while saving.");
    }
  }

  async function onEdit(id: number) {
    try {
      setBusyMessage("Loading part...");
      const row = await runWithLoading(async (): Promise<PartRow | null> => {
        const editOperation = await getListById(ENDPOINT.PARTS_LIST_BY_ID, id);
        const raw = editOperation as unknown;
        if (Array.isArray(raw) && raw.length > 0) {
          return raw[0] as PartRow;
        }
        if (raw && typeof raw === "object" && "id" in (raw as object)) {
          return raw as PartRow;
        }
        return null;
      });
      setBusyMessage("Loading parts...");
      if (!row) {
        toast.error("Could not load part for edit.");
        return;
      }
      setEditId(row.id);
      const cleanedPn = sanitizeStoredPartNumber(row.partnumber);
      let partnumber = cleanedPn;
      if (!partnumber) {
        try {
          const rows = await fetchPartsCatalogForSuggestion();
          partnumber = suggestNextPartNumber(rows);
        } catch {
          partnumber = suggestNextPartNumber(partsCollection);
        }
      }
      setForm({
        partname: row.partname ?? "",
        partnumber,
        materialcategory: row.materialcategory ?? "",
        drawingref: row.drawingref ?? "",
        defaulttolerance: row.defaulttolerance ?? "",
        imagrurl: row.imagrurl ?? "",
        description: row.description ?? "",
      });
      setSelectedMaterialIds(parsePartMaterialIds(row.material));
      setValidated(false);
      setAddUpdateModal(true);
    } catch (e) {
      console.log(`Error in editParts: ${e}`);
      toast.error("Could not open part for edit.");
    }
  }

  async function onDelete(list: PartRow) {
    const isConfirmed = await confirm();
    if (!isConfirmed) return;

    try {
      setBusyMessage("Updating...");
      const payload = {
        ...list,
        active: false,
      };
      await runWithLoading(async () => {
        const saveOperation = await updateList(ENDPOINT.PARTS_UPDATE, payload);
        if (saveOperation === "success") {
          setBusyMessage("Loading parts...");
          await loadPartsData(pageIndex, searchText);
          toast.success("Part removed from catalog.");
        } else {
          toast.error("Could not delete part.");
        }
      });
    } catch (e) {
      console.log(`Error in deleteParts: ${e}`);
      toast.error("Delete failed.");
    }
  }

  const onLoad = useCallback(
    async (nextPageIndex: number = 0, overrideSearchText?: string) => {
      try {
        setBusyMessage("Loading parts...");
        await runWithLoading(async () => {
          await loadPartsData(nextPageIndex, overrideSearchText);
        });
      } catch (e) {
        console.log(`Error in getparts: ${e}`);
      }
    },
    [runWithLoading, loadPartsData],
  );

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showPagination = totalPages > 1;
  const hasRecords = partsCollection?.length > 0;

  const modalTitle = useMemo(
    () => (editId ? "Edit Part" : "Create Part"),
    [editId],
  );

  return (
    <div className="parts-catalog-page mes-partner-portal">
      {isLoading && <AppLoader text={busyMessage} />}
      <div className="parts-catalog tv-page-content">
          <div className="parts-catalog__shell">
            <div className="parts-catalog__head">
              <button
                type="button"
                className="parts-catalog__add-btn"
                onClick={handleShow}
              >
                <i className="fa fa-plus" aria-hidden />
                Add Part
              </button>
            </div>

            <div className="parts-catalog__search-wrap">
              <i
                className="fa fa-search parts-catalog__search-icon"
                aria-hidden
              />
              <input
                type="text"
                placeholder="Search Parts"
                className="parts-catalog__search-input"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onLoad(0, (e.target as HTMLInputElement).value);
                  }
                }}
              />
            </div>

            {!hasRecords ? (
              <MesEmptyState
                icon="fa-cubes"
                title="No Parts Found"
                subtitle="Add your first part to start building workflows."
              />
            ) : (
              <div className="parts-catalog__table-wrap">
                <Table
                  borderless
                  responsive
                  className="parts-catalog__table mb-0"
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Part Number</th>
                      <th>Material</th>
                      <th>Description</th>
                      <th>Created On</th>
                      <th className="parts-catalog__th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partsCollection.map((list) => (
                      <tr key={list.id}>
                        <td className="parts-catalog__cell-name">
                          {list.partname || "—"}
                        </td>
                        <td
                          className={
                            list.partnumber?.trim()
                              ? "parts-catalog__cell-mono"
                              : ""
                          }
                        >
                          {list.partnumber?.trim() ? (
                            list.partnumber
                          ) : (
                            <span className="parts-catalog__cell-muted">—</span>
                          )}
                        </td>
                        <td className="parts-catalog__cell-mat">
                          {formatMaterialCell(
                            list,
                            resolveCategoryLabel,
                            resolveMaterialLabel,
                          )}
                        </td>
                        <td
                          className={
                            list.description?.trim()
                              ? "parts-catalog__cell-desc"
                              : ""
                          }
                          title={
                            list.description?.trim()
                              ? list.description
                              : undefined
                          }
                        >
                          {list.description?.trim() ? (
                            list.description
                          ) : (
                            <span className="parts-catalog__cell-muted">—</span>
                          )}
                        </td>
                      <td>
                        {list.createdate ? (
                          <Moment format="DD MMM YYYY">{list.createdate}</Moment>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div className="parts-catalog__actions">
                          <button
                            type="button"
                            className="parts-catalog__action-btn"
                            title="Edit"
                            onClick={() => onEdit(list.id)}
                          >
                            <i className="fa fa-pencil" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="parts-catalog__action-btn parts-catalog__action-btn--danger"
                            title="Delete"
                            onClick={() => onDelete(list)}
                          >
                            <i className="fa fa-trash-o" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            )}

            {showPagination && (
            <div className="paginate mt-10 tv-list-pagination">
              <nav aria-label="Parts pagination">
                <ul className="pagination">
                  <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                    <a
                      className="page-link"
                      href="#"
                      aria-label="Previous"
                      onClick={(e) => {
                        e.preventDefault();
                        if (pageIndex > 0) onLoad(pageIndex - 1);
                      }}
                    >
                      <span aria-hidden="true">&laquo;</span>
                    </a>
                  </li>

                  {Array.from({ length: totalPages }).map((_, i) => (
                    <li
                      key={i}
                      className={`page-item ${i === pageIndex ? "active" : ""}`}
                    >
                      <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onLoad(i);
                        }}
                      >
                        {i + 1}
                      </a>
                    </li>
                  ))}

                  <li
                    className={`page-item ${
                      pageIndex >= totalPages - 1 ? "disabled" : ""
                    }`}
                  >
                    <a
                      className="page-link"
                      href="#"
                      aria-label="Next"
                      onClick={(e) => {
                        e.preventDefault();
                        if (pageIndex < totalPages - 1) onLoad(pageIndex + 1);
                      }}
                    >
                      <span aria-hidden="true">&raquo;</span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
            )}
          </div>
        </div>

      <Modal
        className="parts-catalog-modal"
        size="lg"
        centered
        show={openAddUpdateModal}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton={false} className="parts-catalog-modal__header">
          <div>
            <Modal.Title as="h2" className="parts-catalog-modal__title">
              {modalTitle}
            </Modal.Title>
            <p className="parts-catalog-modal__subtitle">
              Master data entry point. Part must be fully defined before use in
              orders.
            </p>
          </div>
          <button
            type="button"
            className="parts-catalog-modal__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="parts-catalog-modal__body">
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            id="parts-catalog-form"
          >
            <Form.Group className="mb-3" controlId="partname">
              <Form.Label>Part Name</Form.Label>
              <Form.Control
                required
                type="text"
                name="partname"
                value={formInputs.partname || ""}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">
                Part name is required.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="partnumber">
              <Form.Label>Part Number (auto-generated suggestion)</Form.Label>
              <Form.Control
                type="text"
                name="partnumber"
                placeholder={PART_NUMBER_PLACEHOLDER}
                value={formInputs.partnumber || ""}
                onChange={handleChange}
                onFocus={() => {
                  if (editId) return;
                  void (async () => {
                    const rows = await fetchPartsCatalogForSuggestion();
                    const next = suggestNextPartNumber(rows);
                    setForm((prev) =>
                      sanitizeStoredPartNumber(prev.partnumber)
                        ? prev
                        : { ...prev, partnumber: next },
                    );
                  })();
                }}
              />
              <Form.Text className="text-muted" style={{ fontSize: 12 }}>
                Next free code is filled automatically; edit if needed.
              </Form.Text>
            </Form.Group>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="materialcategory">
                <Form.Label>Material Category</Form.Label>
                <Form.Select
                  className="custom-select"
                  name="materialcategory"
                  value={formInputs.materialcategory || ""}
                  onChange={handleChange}
                >
                  <option value="">Select category…</option>
                  {materialCategories.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="material">
                <Form.Label>Material</Form.Label>
                <RecipeGradeMultiSelect
                  disabled={!formInputs.materialcategory}
                  grades={materialGrades}
                  value={selectedMaterialIds}
                  onChange={setSelectedMaterialIds}
                  placeholder={
                    formInputs.materialcategory
                      ? "Search and select materials…"
                      : "Select a category first"
                  }
                />
              </Form.Group>
            </Row>

            <Form.Group className="mb-3" controlId="drawingref">
              <Form.Label>Drawing Reference</Form.Label>
              <Form.Control
                type="text"
                name="drawingref"
                placeholder="e.g. DWG-2024-001"
                value={formInputs.drawingref || ""}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="defaulttolerance">
              <Form.Label>Default Tolerance</Form.Label>
              <Form.Control
                type="text"
                name="defaulttolerance"
                placeholder="e.g. ±0.01mm"
                value={formInputs.defaulttolerance || ""}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="imagrurl">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="text"
                name="imagrurl"
                value={formInputs.imagrurl || ""}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formInputs.description || ""}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="parts-catalog-modal__footer">
          <Button
            type="button"
            variant="light"
            className="parts-catalog-modal__btn-cancel"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="parts-catalog-form"
            className="parts-catalog-modal__btn-save"
          >
            Save Part
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog />
    </div>
  );
};

export default MesParts;
